import type { Match } from '@uefadigital/panenka';
import { useEffect, useState } from 'react';

import { UEFA_BASE_URL } from 'utils/uefaBaseUrl';

const TYPES: Record<string, string> = {
  LU: 'lineups',
  HR: 'half_time_report',
  FR: 'full_time_report',
  SPS: 'players_statistics',
  TS: 'teams_statistics',
  TPD: 'team_players_passing_distribution',
};

const ucl = {
  DEFAULT: {
    baseUrl: `${UEFA_BASE_URL}/newsfiles/UCL/`,
    allowedTypes: ['LU', 'HR', 'FR', 'SPS', 'TS', 'TPD'],
  },
};

const uel = {
  DEFAULT: {
    baseUrl: `${UEFA_BASE_URL}/newsfiles/uefacup/`,
    allowedTypes: ['LU', 'FR'],
  },
  FINAL: {
    baseUrl: `${UEFA_BASE_URL}/newsfiles/uefacup/`,
    allowedTypes: ['LU', 'HR', 'FR', 'SPS', 'TS', 'TPD'],
  },
};

const uecl = {
  DEFAULT: {
    baseUrl: `${UEFA_BASE_URL}/newsfiles/uefaeuropaconferenceleague/`,
    allowedTypes: ['LU', 'FR'],
  },
  FINAL: {
    baseUrl: `${UEFA_BASE_URL}/newsfiles/uefaeuropaconferenceleague/`,
    allowedTypes: ['LU', 'HR', 'FR', 'SPS', 'TS', 'TPD'],
  },
};

const uwcl = {
  DEFAULT: {
    baseUrl: `${UEFA_BASE_URL}/newsfiles/uwcl/`,
    allowedTypes: ['LU', 'FR'],
  },
};

const unl = {
  DEFAULT: {
    baseUrl: `${UEFA_BASE_URL}/newsfiles/UNL/`,
    allowedTypes: ['LU', 'FR'],
  },
  FINAL: {
    baseUrl: `${UEFA_BASE_URL}/newsfiles/UNL/`,
    allowedTypes: ['LU', 'HR', 'FR', 'SPS', 'TS', 'TPD'],
  },
};

const wc = {
  DEFAULT: {
    baseUrl: `${UEFA_BASE_URL}/newsfiles/EURO/`,
    allowedTypes: ['LU', 'FR'],
  },
};

const scup = {
  DEFAULT: {
    baseUrl: `${UEFA_BASE_URL}/newsfiles/SuperCup/`,
    allowedTypes: ['LU', 'HR', 'FR', 'SPS', 'TS', 'TPD'],
  },
};

const weuro = {
  DEFAULT: {
    baseUrl: `${UEFA_BASE_URL}/newsfiles/WEURO/`,
    allowedTypes: ['LU', 'HR', 'FR', 'SPS'],
  },
};

const u21 = {
  DEFAULT: {
    baseUrl: `${UEFA_BASE_URL}/newsfiles/Under21/`,
    allowedTypes: ['LU', 'FR'],
  },
};

const euro = {
  DEFAULT: {
    baseUrl: `${UEFA_BASE_URL}/newsfiles/EURO/`,
    allowedTypes: ['LU', 'HR', 'FR', 'SPS', 'TS', 'TPD'],
  },
  QUALIFYING: {
    baseUrl: `${UEFA_BASE_URL}/newsfiles/EURO/`,
    allowedTypes: ['LU', 'FR'],
  },
  FINAL_TOURNAMENT_PLAY_OFF: {
    baseUrl: `${UEFA_BASE_URL}/newsfiles/EURO/`,
    allowedTypes: ['LU', 'FR'],
  },
};

const friendly = {
  DEFAULT: {
    baseUrl: `${UEFA_BASE_URL}/newsfiles/Friendly/`,
    allowedTypes: ['LU', 'FR'],
  },
};

interface TournamentData {
  baseUrl?: string;
  isUpperCase?: boolean;
  extraPart?: string;
  allowedTypes?: Array<string>;
}

interface TournamentStageData {
  [key: string]: TournamentData;
}

interface Link {
  name: string;
  link: string;
}

interface PromiseFulfilledResult {
  status: 'fulfilled';
  value: Response;
}

interface PromiseRejectedResult {
  status: 'rejected';
  reason: unknown;
}

type PromiseSettledResult = PromiseFulfilledResult | PromiseRejectedResult;

const tournamentsStore: Record<string, TournamentStageData> = {
  ucl,
  uel,
  uecl,
  scup,
  uwcl,
  wc,
  unl,
  u21,
  weuro,
  euro,
  'a int.': friendly,
};

const getCurrentTournament = (tournamentCode: string) =>
  tournamentsStore[tournamentCode.toLowerCase()] ?? tournamentsStore.ucl;

const getCurrentStage = (match: Match, tournament: TournamentStageData) => {
  const roundType = match.round?.metaData?.type ?? '';

  if (roundType in tournament) {
    return tournament[roundType];
  }

  return tournament['DEFAULT'];
};

export const generatePdfLinks = (match: Match) => {
  const currentTournament = getCurrentTournament(match?.competition?.code ?? '');
  const currentTournamentStage = getCurrentStage(match, currentTournament);

  const baseLink = `${currentTournamentStage.baseUrl}${match.seasonYear}/${match.id}_`;
  const pdfLinksArr: Array<Link> = [];

  if (Array.isArray(currentTournamentStage?.allowedTypes)) {
    currentTournamentStage?.allowedTypes?.forEach((type: string) => {
      pdfLinksArr.push({
        name: TYPES[type],
        link: `${baseLink}${type}.pdf`,
      });
    });
  }

  return pdfLinksArr;
};

export function useAvailablePdfLinks(match: Match, enable: boolean) {
  const [validPdfLinks, setValidPdfLinks] = useState<Array<Link>>([]);
  const pdfLinks = generatePdfLinks(match);
  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (!enable || !pdfLinks.length || loaded) return;

    Promise.allSettled(pdfLinks.map((pdfLink) => fetch(pdfLink.link, { method: 'HEAD' })))
      .then((responses: Array<PromiseSettledResult>) => {
        return responses.reduce((acc: Array<Link>, response) => {
          if (response.status === 'fulfilled' && response.value.ok) {
            const pdfLink = pdfLinks.find((link) => link.link === response.value.url);

            if (pdfLink) {
              return [...acc, pdfLink];
            }
          }

          return acc;
        }, []);
      })
      .then(setValidPdfLinks)
      .then(() => {
        setLoaded(true);
      });
  }, [enable, pdfLinks, loaded]);

  return { links: validPdfLinks, loaded: loaded };
}
